/**
 * When this tab makes changes, the views refetch once, after the last change in flight has
 * finished, not after each one. A refetch started for an earlier change would be cancelled by
 * the next one, and a cancelled request still runs on the server (which applies one user's
 * changes one at a time): ten quick filings queued dozens of reads, and the counts stayed stale
 * for seconds (found in the keyboard triage test). Live events wait the same way.
 */

type Invalidator = {
  invalidateQueries: (filters: { queryKey: readonly unknown[] }) => Promise<unknown>
}

export function createRefresher() {
  let running = 0
  let client: Invalidator | null = null
  const stale = new Set<string>()

  const flush = () => {
    if (!client) return
    const roots = [...stale]
    stale.clear()
    for (const root of roots) void client.invalidateQueries({ queryKey: [root] })
  }

  return {
    /** A change starts; call the returned function when it has finished (once). */
    begin(): () => void {
      running += 1
      let ended = false
      return () => {
        if (ended) return
        ended = true
        running -= 1
        if (running === 0) flush()
      }
    },
    /** Refetch these query families now, or once this tab's changes in flight have finished. */
    refresh(target: Invalidator, roots: readonly string[]) {
      client = target
      for (const root of roots) stale.add(root)
      if (running === 0) flush()
    },
  }
}

/** The app's one refresher: tool calls and the live stream share it. */
export const refresher = createRefresher()
